import React from 'react';
import { Settings, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import CurrencyDisplay from '../../../components/CurrencyDisplay';

/**
 * Salary Tiers configuration modal — extracted from DetailedReports.js.
 * Pure presentational/controlled component: parent owns the state.
 */
const SalaryTiersModal = ({
  open,
  language,
  tiers,
  newTier,
  onNewTierChange,
  onAddTier,
  onRemoveTier,
  onSave,
  onClose,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="salary-tiers-modal">
      <div className="bg-card rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          {language === 'ar' ? 'إدارة شرائح الرواتب' : 'Manage Salary Tiers'}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {language === 'ar'
            ? 'حدد حتى 4 شرائح للرواتب بناءً على إيراد كل حلاق الشهري. اترك "إلى" فارغاً للشريحة الأخيرة (وأكثر).'
            : 'Define up to 4 salary tiers based on each barber\'s monthly revenue. Leave "To" empty for the last tier (and above).'}
        </p>

        {/* Example */}
        <div className="mb-4 p-3 bg-blue-500/10 rounded-lg text-sm">
          <p className="font-semibold mb-1">{language === 'ar' ? 'مثال:' : 'Example:'}</p>
          <ul className="list-disc list-inside text-muted-foreground text-xs space-y-1">
            <li>0 - 4000 → 30%</li>
            <li>4001 - 6000 → 40%</li>
            <li>6001+ → 50%</li>
          </ul>
        </div>

        {/* Current Tiers */}
        {tiers.length > 0 && (
          <div className="mb-4 space-y-2">
            {tiers.map((tier, index) => (
              <div key={`tier-${tier.revenue_from}-${tier.revenue_to}-${tier.percentage}`} className="flex items-center gap-2 p-3 bg-secondary/50 rounded-lg">
                <div className="flex-1 grid grid-cols-3 gap-2 text-sm">
                  <span>{language === 'ar' ? 'من' : 'From'}: <CurrencyDisplay amount={tier.revenue_from} /></span>
                  <span>
                    {language === 'ar' ? 'إلى' : 'To'}: {
                      tier.revenue_to === 0 || tier.revenue_to >= 999999
                        ? (language === 'ar' ? 'وأكثر ∞' : '∞ and above')
                        : <CurrencyDisplay amount={tier.revenue_to} />
                    }
                  </span>
                  <span className="text-primary font-bold">{tier.percentage}%</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveTier(index)}
                  className="text-red-500 hover:text-red-600"
                  data-testid={`salary-tier-remove-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add New Tier */}
        {tiers.length < 4 && (
          <div className="space-y-2 mb-4">
            <div className="grid grid-cols-4 gap-2">
              <Input
                type="number"
                placeholder={language === 'ar' ? 'من' : 'From'}
                value={newTier.revenue_from}
                onChange={(e) => onNewTierChange({ ...newTier, revenue_from: e.target.value })}
                className="bg-secondary/50"
                data-testid="salary-tier-from-input"
              />
              <Input
                type="number"
                placeholder={language === 'ar' ? 'إلى (فارغ=وأكثر)' : 'To (empty=∞)'}
                value={newTier.revenue_to}
                onChange={(e) => onNewTierChange({ ...newTier, revenue_to: e.target.value })}
                className="bg-secondary/50"
                data-testid="salary-tier-to-input"
              />
              <Input
                type="number"
                placeholder="%"
                value={newTier.percentage}
                onChange={(e) => onNewTierChange({ ...newTier, percentage: e.target.value })}
                className="bg-secondary/50"
                max="100"
                data-testid="salary-tier-pct-input"
              />
              <Button onClick={onAddTier} variant="outline" className="gap-1" data-testid="salary-tier-add-btn">
                <Plus className="w-4 h-4" />
                {language === 'ar' ? 'إضافة' : 'Add'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {language === 'ar'
                ? '* اترك حقل "إلى" فارغاً للشريحة الأخيرة لتعني "وأكثر"'
                : '* Leave "To" empty for the last tier to mean "and above"'}
            </p>
          </div>
        )}

        {/* Modal Actions */}
        <div className="flex gap-2 justify-end mt-6">
          <Button variant="outline" onClick={onClose} data-testid="salary-tiers-cancel">
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={onSave} data-testid="salary-tiers-save">
            {language === 'ar' ? 'حفظ الشرائح' : 'Save Tiers'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SalaryTiersModal;
